import { type JSX, useMemo } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import { differenceInSeconds } from 'date-fns';
import { secondsInYear } from 'date-fns/constants';
import { Link } from '@/shared/components/atoms/Link';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { type GetDelegationHistoryQuery } from '@/shared/graphql/generated/explorer/graphql';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import FlipAmount from '@/shared/utils/FlipAmount';
import { formatPct } from '@/shared/utils/strings';
import { type TokenCast } from '@/shared/utils/TokenAmount';
import { getDelegationHistoryQuery } from '../../../graphql/explorer/delegatorQueries';

type DelegationHistory = NonNullable<
  GetDelegationHistoryQuery['allOperatorEpochDelegations']
>['edges'][number]['node'];
const delegationHistoryCastFields = ['amount', 'rewards'] as const;
type CastDelegationHistory = TokenCast<
  DelegationHistory,
  (typeof delegationHistoryCastFields)[number]
>;

const DelegationHistoryTableRow = ({
  item: {
    amount,
    rewards,
    operatorEpoch: { epoch, operator },
  },
}: {
  item: CastDelegationHistory;
}) => {
  const epochDurationSeconds = differenceInSeconds(
    epoch.endBlock?.timestamp ?? new Date(),
    epoch.startBlock.timestamp,
  );
  const apy =
    epochDurationSeconds > 0 && amount.gt(0)
      ? formatPct(
          rewards
            .div(amount.gt(0) ? amount.toBigNumber() : 1)
            .div(epochDurationSeconds > 0 ? epochDurationSeconds : 1)
            .mul(secondsInYear)
            .toNumber(),
        )
      : 'N/A';

  return (
    <tr className="text-14">
      <td>
        <Link
          className="underline"
          href={buildChainflipExplorerLink(epoch.id.toString(), 'authority-sets')}
          noPropagate
        >
          Authority Set {epoch.id}
        </Link>
      </td>
      <td>
        <div className="flex flex-col gap-y-2">
          <div className="flex flex-col">
            {operator.account.alias}
            <div className="flex items-center gap-x-1">
              <Link
                href={buildChainflipExplorerLink(operator.account.idSs58, 'operators')!}
                target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
                rel="noreferrer"
                underline
                noPropagate
                color="light"
                className="whitespace-nowrap"
              >
                {abbreviate(operator.account.idSs58, 4)}
              </Link>
              <CopyButton textToCopy={operator.account.idSs58} />
            </div>
          </div>
        </div>
      </td>
      <td>
        <FlipLabel amount={amount} />
      </td>
      <td className="whitespace-nowrap">
        <FlipLabel amount={rewards} />
      </td>
      <td className="whitespace-nowrap">{apy}</td>
      <td className="whitespace-nowrap text-right text-12 text-cf-light-2">
        {new Date(epoch.startBlock.timestamp).toLocaleDateString()} -{' '}
        {epoch.endBlock ? new Date(epoch.endBlock.timestamp).toLocaleDateString() : 'current'}
      </td>
    </tr>
  );
};
export const DelegationHistoryTable = (): JSX.Element => {
  const { idSs58: delegatorIdSs58 } = useEvmScAccount(true);

  const {
    data: delegationHistoryPaginated,
    isLoading: delegationHistoryLoading,
    error,
    paginationControls,
  } = useRemotePagination(getDelegationHistoryQuery, {
    variables: { delegationIdSs58: delegatorIdSs58 },
    enabled: isNotNullish(delegatorIdSs58),
    useQueryParam: true,
    totalCount: (data) => data.allOperatorEpochDelegations?.totalCount ?? 0,
  });

  const optimizedRows = useMemo(
    () =>
      FlipAmount.castObject(
        delegationHistoryPaginated,
        delegationHistoryCastFields,
      )?.allOperatorEpochDelegations?.edges.map(({ node: history }) => (
        <DelegationHistoryTableRow key={history.id} item={history} />
      )) ?? [],
    [delegationHistoryPaginated],
  );

  if (error) throw error;
  return (
    <TableV2
      className="rounded-t-none"
      columns={[
        'Authority Set',
        'Operator',
        'Delegated Amount',
        'Rewards',
        'APY',
        { alignment: 'right', name: 'Date Range' },
      ]}
      rows={optimizedRows}
      isLoading={delegationHistoryLoading}
      paginationControls={paginationControls}
    />
  );
};
